import { useEffect, useState } from "react";
import { router } from "@/app/routes";
import {
  loadGA4,
  loadMetaPixel,
  readConsent,
  writeConsent,
} from "@/lib/cookie-consent";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";

/** Show the consent card after this long if the visitor has not scrolled. */
const REVEAL_FALLBACK_MS = 8000;

/**
 * Minimal cookie consent banner. Shown once to every visitor until a
 * choice is stored in localStorage. Designed for button parity (2025
 * Austria ruling) and prior-consent compliance (GDPR/ePrivacy): neither
 * GA4 nor the Meta Pixel loads until the user explicitly accepts.
 *
 * REVEAL TIMING (2026-09). The card sits bottom-left and covered the hero's
 * investor band on a first visit, exactly when that band matters most. So a
 * first-time visitor does not see the card until their first scroll, with a
 * fallback timer for someone who never scrolls. Delaying the card is
 * compliant because nothing tracks before Accept. "Cookie preferences" in the
 * footer (`forceOpen`) still opens it immediately.
 *
 * All visual tokens are shared with the rest of the landing surface —
 * see Task 3 in the plan file for the full design rationale.
 */

export function CookieConsent() {
  const [hasChosen, setHasChosen] = useState(true);
  const [engaged, setEngaged] = useState(false);
  const forceOpen = useCookieConsentStore((s) => s.forceOpen);
  const dismiss = useCookieConsentStore((s) => s.dismiss);

  useEffect(() => {
    const stored = readConsent();
    if (stored === "accepted") {
      loadGA4();
      loadMetaPixel();
      setHasChosen(true);
      return;
    }
    if (stored === "declined") {
      setHasChosen(true);
      return;
    }
    setHasChosen(false);

    // No choice yet: reveal on the first scroll, or after the fallback.
    const reveal = () => setEngaged(true);
    window.addEventListener("scroll", reveal, { once: true, passive: true });
    const timer = window.setTimeout(reveal, REVEAL_FALLBACK_MS);
    return () => {
      window.removeEventListener("scroll", reveal);
      window.clearTimeout(timer);
    };
  }, []);

  const visible = (!hasChosen && engaged) || forceOpen;
  if (!visible) return null;

  function handleAccept() {
    writeConsent("accepted");
    loadGA4();
    loadMetaPixel();
    // Undoes a Decline made earlier in this page session.
    window.fbq?.("consent", "grant");
    setHasChosen(true);
    dismiss();
  }

  function handleDecline() {
    writeConsent("declined");
    // Stops a pixel that an earlier Accept loaded in this page session.
    window.fbq?.("consent", "revoke");
    setHasChosen(true);
    dismiss();
  }

  function handlePrivacyClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    // Use the router instance directly — component lives outside
    // RouterProvider so `useNavigate` isn't available here.
    router.navigate("/privacy");
  }

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed z-[60]"
      style={{
        left: "clamp(16px, 2vw, 20px)",
        right: "clamp(16px, 2vw, 20px)",
        bottom: "clamp(16px, 2vw, 20px)",
        maxWidth: 480,
      }}
    >
      <div
        style={{
          background: "hsl(var(--background) / 0.92)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid hsl(var(--foreground) / 0.08)",
          borderRadius: 16,
          boxShadow: "var(--shadow-lg)",
          padding: "18px 20px",
        }}
      >
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "hsl(var(--foreground) / 0.80)",
            margin: 0,
          }}
        >
          We use analytics and advertising cookies to understand how Altair is
          used, improve the product, and measure our ads.
        </p>

        <div
          className="mt-4 flex items-center gap-2.5 flex-wrap"
          style={{ rowGap: 10 }}
        >
          <button
            type="button"
            onClick={handleDecline}
            className="inline-flex items-center justify-center rounded-full text-[14px] font-medium transition-all duration-150 text-foreground hover:bg-foreground/5 cursor-pointer"
            style={{
              padding: "8px 20px",
              border: "1px solid hsl(var(--primary) / 0.18)",
              background: "transparent",
            }}
          >
            Decline
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="inline-flex items-center justify-center rounded-full text-[14px] font-medium transition-all duration-150 text-foreground hover:bg-foreground/5 cursor-pointer"
            style={{
              padding: "8px 20px",
              border: "1px solid hsl(var(--primary) / 0.18)",
              background: "transparent",
            }}
          >
            Accept
          </button>
          <a
            href="/privacy"
            onClick={handlePrivacyClick}
            className="no-underline transition-colors duration-150"
            style={{
              fontSize: 13,
              color: "hsl(var(--foreground) / 0.60)",
              marginLeft: "auto",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground) / 0.60)";
            }}
          >
            Privacy policy
          </a>
        </div>
      </div>
    </div>
  );
}
